import React from "react";
import Heading from "../Heading/Heading";
import ListItemWithIcon from "../ListItems/ListItemWithIcon";
import FeeContent from "../FeeContent/FeeContent";
import {
  Element,
  ElementRendererProps,
} from "../../../Interface/scheduler/form";
import {
  conceptCodes,
  SKELETON_LOADER_LAYOUT_FORM,
} from "../../../constants/constant";
import { SUSPENSE_WRAPPER } from "../SuspenseWrapper/SuspenseWrapper";
import useIsMobile from "../../hooks/useIsMobile";
import TextField from "../TextField/TextField";
import { RenderFormElement } from "../FormElement/FormElement";
import ImageElement from "../Image/Image";
import WrapperElement from "../WrapperElement/WrapperElement";
import OfferDetailsFooter from "../../../feature/scheduler/components/OffersPage/OfferDetailsFooter";
import { useFormConfigContext } from "../../contexts/FormConfigContext";
import { landingPageOfferSourceValidation } from "../../../utility/utility";
import OffersApply from "../FormBuilder/OffersApply";
import PropertyAreaWrapper from "../../../feature/propertyAreaConfirmation/PropertyAreaWrapper";
import ServiceSelector from "../../../feature/propertyAreaConfirmation/ServiceSelector/ServiceSelector";
import { getBannerPosition } from "../../../feature/scheduler/components/SchedulerStep/SchedulerStepHelper";
//Lazy Loading JUK Pricing Component
const LazyRenderServiceTabs = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../../../feature/servicePricingTabs/ServiceTabs"
    )
);

const ElementRenderer = ({
  element,
  errors,
  setErrors,
  formActionButtons,
  displaySpecialOffer,
  offerDetails,
  applyOffer,
}: ElementRendererProps) => {
  const isMobile = useIsMobile(element as Element);
  const { formConfig } = useFormConfigContext();
  const { conceptCode, feeDetails } = formConfig;

  const OffersFooterComponent = () => (
    <OfferDetailsFooter offerDetails={offerDetails} offerRemoved={false} />
  );

  const isJUKServicePage = () => {
    if (
      element.id === "how-we-can-reach-form" &&
      formConfig.conceptCode === conceptCodes.JUK_CONCEPT_CODE
    ) {
      return true;
    }
  };

  switch (element.type) {
    case "heading":
      return <Heading element={element as Element} />;
    case "form":
      return (
        <>
          <RenderFormElement
            element={element as Element}
            errors={errors}
            setErrors={setErrors}
          />
          {isMobile && !isJUKServicePage() && (
            <>
              {displaySpecialOffer && formConfig.offerPosition === "bottom" && (
                <OffersFooterComponent />
              )}
              {!landingPageOfferSourceValidation() && (
                <OffersApply field={applyOffer} setErrors={setErrors} />
              )}
              <div style={{ marginBottom: 20 }}>{formActionButtons}</div>
            </>
          )}
        </>
      );
    case "image":
      return <ImageElement element={element as Element} />;
    case "list":
      return <ListItemWithIcon element={element} />;
    case "text":
      return <TextField element={element as Element} />;
    case "fee":
      return (
        <FeeContent
          element={element as Element}
          conceptCode={conceptCode}
          feeDetails={feeDetails}
        />
      );
    case "wrapperElement":
      return (
        <WrapperElement
          element={element}
          conceptCode={conceptCode}
          renderChild={(child) => (
            <ElementRenderer
              key={child.type}
              element={child}
              errors={errors}
              setErrors={setErrors}
            />
          )}
        />
      );
    case "servicePricingTabs":
      return (
        <>
          {SUSPENSE_WRAPPER(
            <LazyRenderServiceTabs
              errors={errors}
              setErrors={setErrors}
              serviceErrorMessage={(element as Element).serviceErrorMessage}
            />,
            SKELETON_LOADER_LAYOUT_FORM
          )}
          {isMobile && (
            <>
              <div style={getBannerPosition(applyOffer?.showBannerOfferOnTop)}>
                {displaySpecialOffer &&
                  formConfig.offerPosition === "bottom" && (
                    <OffersFooterComponent />
                  )}
                {!landingPageOfferSourceValidation() && (
                  <OffersApply field={applyOffer} setErrors={setErrors} />
                )}
              </div>
              <div style={{ marginBottom: 20 }}>{formActionButtons}</div>
            </>
          )}
        </>
      );
    case "propertyAreaConfirmation":
      return (
        <PropertyAreaWrapper
          element={element as Element}
          errors={errors}
          setErrors={setErrors}
        />
      );
    case "serviceSelector":
      return (
        <>
          <ServiceSelector
            element={element as Element}
            errors={errors}
            setErrors={setErrors}
          />
          {isMobile && (
            <div style={{ marginBottom: 20 }}>{formActionButtons}</div>
          )}
        </>
      );
    default:
      return null;
  }
};

export default ElementRenderer;
